import { TextField, TextFieldProps, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, InputAdornment } from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useState } from 'react';
import { styled } from '@mui/material/styles';

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  dialogTitle?: string;
};

export const ExpandableTextFieldForm = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  dialogTitle = 'Éditer le texte',
  ...props
}: Props<TFieldValues, TName> & TextFieldProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules, shouldUnregister, defaultValue });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState(field.value || '');
  const [isHovered, setIsHovered] = useState(false);

  const handleExpand = () => {
    setDialogValue(field.value || '');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = () => {
    field.onChange(dialogValue);
    setDialogOpen(false);
  };

  const handleDialogValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDialogValue(event.target.value);
  };

  return (
    <>
      <Tooltip title={error?.message || ''}>
        <TextField
          {...field}
          error={!!error}
          {...props}
          data-testid={name}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            ...props.sx,
          }}
          InputProps={{
            ...props.InputProps,
            endAdornment: (
              <>
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleExpand}
                  sx={{
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                  className="expand-icon"
                  data-testid={`${name}-expand-button`}
                >
                  <OpenInFull fontSize="small" />
                </IconButton>
              </InputAdornment>
              {props.InputProps?.endAdornment}
              </>
            ),
          }}
        />
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        data-testid={`${name}-dialog`}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            {...field}
            {...props}
            fullWidth
            multiline
            minRows={6}
            maxRows={12}
            value={dialogValue}
            onChange={handleDialogValueChange}
            variant="outlined"
            sx={{ 
              mt: 1,
              ...props.sx,
            }}
            InputProps={{
              ...props.InputProps,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
